/**
 * Contact Sales Associate Block - Complete Initialization
 *
 * Handles all initialization for the contact-sales-associate block including:
 * - Alpine.js component registration and initialization
 * - Google Maps autocomplete setup
 * - GravityForms integration
 * - Event listeners and field configuration
 */

// Module-level variables for Google Maps
let autocomplete;
let addressFieldIds = null;
let input_field = null;

/**
 * Escape an HTML id for use in a CSS attribute selector (#id).
 * @param {string} id
 * @returns {string}
 */
function csaCssIdSelector(id) {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(id);
  }
  return id.replace(/([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, "\\$1");
}

/**
 * Preload a URL for use as background-image so the first paint avoids progressive decode tearing.
 * Resolves even on load error so the UI is not blocked.
 * @param {string} url
 * @returns {Promise<void>}
 */
function csaPreloadRepBackgroundUrl(url) {
  if (!url || typeof url !== "string") {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const img = new Image();
    const finish = () => resolve(img);
    img.onload = () => finish();
    img.onerror = () => finish();
    img.src = url;
    if (img.complete) {
      finish();
    }
  }).then((img) => {
    if (img && typeof img.decode === "function") {
      return img.decode().catch(() => {});
    }
  });
}

/**
 * Dynamically detect the GravityForms address field and extract field IDs
 * @param {HTMLElement} modal - The modal element
 * @returns {Object|null} Object containing sub-field IDs, or null if not found
 */
function detectAddressField(modal) {
	if (!modal) return null;
	// Look for the address field container
	const addressContainer = modal.querySelector('.ginput_container_address');
	if (!addressContainer) return null;
	// Extract form_id and field_id from the container ID
	// Format: input_{form_id}_{field_id}
	const containerId = addressContainer.id;
	const matches = containerId.match(/^input_(\d+)_(\d+)$/);

	if (!matches) return null;
	const formId = matches[1];
	const fieldId = matches[2];

	// Build sub-field IDs dynamically
	// Format: input_{form_id}_{field_id}_{subfield_number}
	return {
		address1: `input_${formId}_${fieldId}_1`,
		address2: `input_${formId}_${fieldId}_2`,
		city: `input_${formId}_${fieldId}_3`,
		state: `input_${formId}_${fieldId}_4`,
		zipcode: `input_${formId}_${fieldId}_5`,
		country: `input_${formId}_${fieldId}_6`
	};
}

/**
 * Initialize Google Maps autocomplete
 * @param {HTMLElement} modal - The modal element
 */
function initMap(modal) {
  // Dynamically detect the address field
  addressFieldIds = detectAddressField(modal);

  if (addressFieldIds) {
    input_field = modal.querySelector("#" + csaCssIdSelector(addressFieldIds.address1));
  }

  if (
    input_field &&
    typeof google !== "undefined" &&
    google.maps &&
    google.maps.places
  ) {
    autocomplete = new google.maps.places.Autocomplete(input_field, {
      types: ["address"],
      componentRestrictions: { country: "US" },
      fields: ["address_components", "geometry"]
    });

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();

      let address1 = modal.querySelector("#" + csaCssIdSelector(addressFieldIds.address1));
      let address2 = modal.querySelector("#" + csaCssIdSelector(addressFieldIds.address2));
      let city = modal.querySelector("#" + csaCssIdSelector(addressFieldIds.city));
      let state = modal.querySelector("#" + csaCssIdSelector(addressFieldIds.state));
      let zipcode = modal.querySelector("#" + csaCssIdSelector(addressFieldIds.zipcode));

      // Initialize variables to store the address components
      let streetAddress = "";
      let cityName = "";
      let stateName = "";
      let zipCode = "";

      // Iterate over address components to find the relevant information
      place.address_components.forEach((component) => {
        const types = component.types;
        if (types.includes("street_number") || types.includes("route")) {
          streetAddress += component.long_name + " ";
        } else if (types.includes("locality")) {
          cityName = component.long_name;
        } else if (types.includes("administrative_area_level_1")) {
          stateName = component.short_name;
        } else if (types.includes("postal_code")) {
          zipCode = component.long_name;
        }
      });

      // Populate the form fields with the extracted information
      if (address1) address1.value = streetAddress.trim();
      if (address2) address2.value = ""; // Optional: clear address2 if not needed
      if (city) city.value = cityName;
      if (state) state.value = stateName;
      if (zipcode) zipcode.value = zipCode;

      hideGoogleAutoComplete();
    });
    input_field.addEventListener("input", () => {
      if (input_field.value.length === 0) {
        hideGoogleAutoComplete();
      } else {
        adjustPacPosition();
      }
    });

    input_field.addEventListener("blur", function (event) {
      hideGoogleAutoComplete();
    });
  }
}

/**
 * Hide Google Places Autocomplete dropdown
 */
function hideGoogleAutoComplete() {
  const pacs = document.querySelectorAll(".pac-container");

  pacs.forEach((pac) => {
    if (pac) {
      pac.style.display = "none";
      pac.classList.remove("force-show");
    }
  });
}

/**
 * Adjust Google Places Autocomplete dropdown position
 */
function adjustPacPosition() {
  if (!input_field) {
    return;
  }

  setTimeout(() => {
    const pacs = document.querySelectorAll(".pac-container");

    if (pacs && pacs.length > 0) {
      pacs.forEach((pac) => {
        if (pac) {
          if (!input_field) {
            return;
          }

          const rect = input_field.getBoundingClientRect();

          pac.style.position = "absolute";
          pac.style.top = window.scrollY + rect.bottom + "px";
          pac.style.left = window.scrollX + rect.left + "px";
          pac.style.width = rect.width + "px";

          pac.style.display = "block";
          pac.style.visibility = "visible";
          pac.style.zIndex = "9999999";

          pac.classList.add("force-show");
        }
      });
    }
  }, 0);
}

/**
 * Listen to form field changes and dispatch events
 * @param {HTMLElement} modal - The modal element
 */
function csaListenToFields(modal) {
  const address1Field = modal.querySelector(".ginput_address_line_1 input");
  if (address1Field) {
    address1Field.addEventListener("change", function () {
      // Currently commented out in original
    });
  }

  // Listen to country field change
  const countryField = modal.querySelector(".ginput_address_country select");
  if (countryField) {
    countryField.addEventListener("change", function () {
      modal.dispatchEvent(
        new CustomEvent("csa-set-data", {
          detail: {
            userCountry: this.value
          }
        })
      );
    });
  }

  // Listen to state field change
  const stateField = modal.querySelector(".ginput_address_state input");
  if (stateField) {
    stateField.addEventListener("change", function () {
      modal.dispatchEvent(
        new CustomEvent("csa-set-data", {
          detail: {
            userState: this.value
          }
        })
      );
    });
  }

  // Listen to zip field change
  const zipField = modal.querySelector(".ginput_address_zip input");
  if (zipField) {
    zipField.addEventListener("change", function () {
      modal.dispatchEvent(
        new CustomEvent("csa-set-data", {
          detail: {
            userZip: this.value
          }
        })
      );
    });
  }

  // Listen to product field change
  const productField = modal.querySelector(".cirrus-sales-product select");
  if (productField) {
    productField.addEventListener("change", function () {
      modal.dispatchEvent(
        new CustomEvent("csa-set-data", {
          detail: {
            salesProduct: this.value
          }
        })
      );
    });
  }
}

/**
 * Get form field values
 * @param {HTMLElement} modal - The modal element
 * @returns {Object} Filtered form data
 */
function csaGetFields(modal) {
  // Collect all the data
  const data = {
    userCountry: modal.querySelector(".ginput_address_country select")?.value,
    userState: modal.querySelector(".ginput_address_state input")?.value,
    userZip: modal.querySelector(".ginput_address_zip input")?.value,
    salesProduct: modal.querySelector(".cirrus-sales-product select")?.value // Must add class name 'cirrus-sales-product' to the select field in GravityForms
  };

  // Filter out empty values
  const filteredData = Object.fromEntries(
    Object.entries(data).filter(([_, value]) => value != null && value !== "")
  );

  return filteredData;
}

/**
 * Configure form labels for floating label effect
 * @param {HTMLElement} modal - The modal element
 */
function csaConfigureLabels(modal) {
  const form = modal.querySelector("form");

  if (!form) {
    return;
  }

  const fields = form.querySelectorAll(".gfield");

  if (!fields) {
    return;
  }

  fields.forEach((field) => {
    const label = field.querySelector("label");
    const input = field.querySelector("input, textarea");

    if (input && label) {
      if (input.value) {
        label.classList.add("not-empty");
      }

      if (input === document.activeElement) {
        label.classList.add("active");
      }

      input.addEventListener("focus", (e) => {
        label.classList.add("active");
      });

      input.addEventListener("blur", (e) => {
        label.classList.remove("active");
      });

      input.addEventListener("change", (e) => {
        const value = e.target.value;
        if (value) {
          label.classList.add("not-empty");
        } else {
          label.classList.remove("not-empty");
        }
      });
    }
  });
}

/**
 * Keep Gravity Forms progress title in sync for this specific form instance.
 * This avoids incorrect counts when other forms exist on the page.
 *
 * @param {HTMLElement} block - The block wrapper
 * @param {number|null} formId - Gravity Form ID
 * @param {number|string|undefined} currentPage - Current page from gform_post_render
 */
function csaSyncProgressTitle(block, formId, currentPage) {
  if (!block || !formId) {
    return;
  }

  const form = block.querySelector(`#gform_${formId}`);
  if (!form) {
    return;
  }

  const title = block.querySelector(".gf_progressbar_title");
  if (!title) {
    return;
  }

  const pageNodes = form.querySelectorAll(`[id^="gform_page_${formId}_"]`);
  if (!pageNodes.length) {
    return;
  }

  const pageNumbers = new Set();
  pageNodes.forEach((node) => {
    const match = node.id.match(new RegExp(`^gform_page_${formId}_(\\d+)$`));
    if (match) {
      pageNumbers.add(parseInt(match[1], 10));
    }
  });

  const totalPages = pageNumbers.size;
  if (!totalPages) {
    return;
  }

  let page = parseInt(currentPage, 10);
  if (!Number.isFinite(page)) {
    const visiblePage = Array.from(pageNodes).find(
      (node) => window.getComputedStyle(node).display !== "none"
    );
    const visibleMatch = visiblePage?.id.match(
      new RegExp(`^gform_page_${formId}_(\\d+)$`)
    );
    page = visibleMatch ? parseInt(visibleMatch[1], 10) : 1;
  }

  page = Math.min(Math.max(page, 1), totalPages);

  const translate =
    window.wp?.i18n?.__ ||
    ((text) => text);
  const stepLabel = translate("Step", "cirrus-blocks");
  const ofLabel = translate("of", "cirrus-blocks");

  title.textContent = `${stepLabel} ${page} ${ofLabel} ${totalPages}`;
}

/**
 * Get the Alpine.js component definition
 * @returns {Function} Component factory function
 */
function getCirrusSalesAssociateComponent() {
  return () => ({
    city: "",
    country: "",
    email: "",
    name: "",
    open: false,
    phone: "",
    profilePhotoUrl: "",
    salesProduct: "",
    state: "",
    submitted: false,
    territoryName: "",
    userCountry: "",
    userState: "",
    userZip: "",
    prevParams: "",
    hiddenCloseButtons: [],
    geoSalesRepEnabled: false,
    showRepOnSubmit: false,
    geoRepVisible: false,
    geoLookupPending: false,
    geoFallbackRep: null,
    geoFallbackCountry: "",
    geoFallbackState: "",
    geoFallbackZip: "",
    geoFallbackFetchPromise: null,
    repFallbackBgUrl: "",
    geoShowFallbackRep: true,

    init() {
      const el = this.$el;
      if (el && el.dataset) {
        this.geoSalesRepEnabled = el.dataset.geoSalesRep === "1";
        this.showRepOnSubmit = el.dataset.showRepOnSubmit === "1";
        this.geoShowFallbackRep = this.geoSalesRepEnabled
          ? el.dataset.geoShowFallbackRep !== "0"
          : false;
        this.repFallbackBgUrl = el.dataset.repFallbackBg || "";
        this.geoFallbackCountry = el.dataset.geoFallbackCountry || "";
        this.geoFallbackState = el.dataset.geoFallbackState || "";
        this.geoFallbackZip = el.dataset.geoFallbackZip || "";
        const rawFb = el.dataset.geoFallbackRep;
        if (rawFb) {
          try {
            this.geoFallbackRep = JSON.parse(rawFb);
          } catch {
            this.geoFallbackRep = null;
          }
        }
      }
    },

    canFetchGeoFallback() {
      if (!this.geoFallbackCountry) {
        return false;
      }
      if (this.geoFallbackCountry === "United States") {
        return this.geoFallbackState !== "" && this.geoFallbackZip !== "";
      }
      return true;
    },

    fetchGeoFallbackRep() {
      if (
        this.geoFallbackRep &&
        typeof this.geoFallbackRep === "object" &&
        this.geoFallbackRep.name
      ) {
        return Promise.resolve(this.geoFallbackRep);
      }
      if (!this.canFetchGeoFallback() || !window.cirrusBlocks?.restUrl) {
        return Promise.resolve(null);
      }
      if (this.geoFallbackFetchPromise) {
        return this.geoFallbackFetchPromise;
      }

      const base = window.cirrusBlocks.restUrl.replace(/\/$/, "");
      const qs = new URLSearchParams({
        country: this.geoFallbackCountry
      });
      if (this.geoFallbackState) {
        qs.set("state", this.geoFallbackState);
      }
      if (this.geoFallbackZip) {
        qs.set("zip", this.geoFallbackZip);
      }
      if (this.salesProduct) {
        qs.set("product", this.salesProduct);
      }

      const headers = {};
      if (window.cirrusBlocks.restNonce) {
        headers["X-WP-Nonce"] = window.cirrusBlocks.restNonce;
      }

      this.geoFallbackFetchPromise = fetch(
        `${base}/contact-sales-associate?${qs.toString()}`,
        { headers, credentials: "same-origin" }
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error("Geo fallback sales rep request failed");
          }
          return response.json();
        })
        .then((data) => {
          if (!data || typeof data !== "object" || !data.name) {
            return null;
          }
          this.geoFallbackRep = data;
          return data;
        })
        .catch(() => null)
        .finally(() => {
          this.geoFallbackFetchPromise = null;
        });

      return this.geoFallbackFetchPromise;
    },

    repSidePanelVisible() {
      return (
        (this.geoSalesRepEnabled && this.geoRepVisible) ||
        (this.showRepOnSubmit && this.submitted)
      );
    },

    applyRepPayload(data) {
      if (!data || typeof data !== "object") {
        return;
      }
      this.city = data.city || "";
      this.country = data.country || "";
      this.email = data.email || "";
      this.name = data.name || "";
      this.phone = data.phone || "";
      this.profilePhotoUrl = data.profilePhotoUrl || "";
      this.state = data.state || "";
      this.territoryName = data.territoryName || "";
    },

    tryApplyGeoFallback() {
      // If submit-mode is active and the user has submitted, Salesforce is authoritative.
      if (this.showRepOnSubmit && this.submitted) {
        this.geoRepVisible = false;
        this.geoLookupPending = false;
        return Promise.resolve(false);
      }
      if (!this.geoShowFallbackRep) {
        this.geoRepVisible = false;
        this.geoLookupPending = false;
        return Promise.resolve(false);
      }
      return this.fetchGeoFallbackRep().then((rep) => {
        if (!rep || typeof rep !== "object") {
          this.geoRepVisible = false;
          this.geoLookupPending = false;
          return false;
        }
        this.applyRepPayload(rep);
        const url = this.profilePhotoUrl || this.repFallbackBgUrl;
        return csaPreloadRepBackgroundUrl(url)
          .then(() => {
            this.geoRepVisible = true;
            this.geoLookupPending = false;
            return true;
          })
          .catch(() => {
            this.geoRepVisible = true;
            this.geoLookupPending = false;
            return true;
          });
      });
    },

    fetchGeoSalesRep() {
      if (!this.geoSalesRepEnabled) {
        return;
      }
      if (this.showRepOnSubmit && this.submitted) {
        this.geoRepVisible = false;
        this.geoLookupPending = false;
        return;
      }
      this.geoLookupPending = true;
      this.geoRepVisible = false;
      if (!window.cirrusBlocks?.restUrl) {
        this.tryApplyGeoFallback();
        return;
      }
      if (typeof navigator === "undefined" || !navigator.geolocation) {
        this.tryApplyGeoFallback();
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          const base = window.cirrusBlocks.restUrl.replace(/\/$/, "");
          const qs = new URLSearchParams({
            lat: String(lat),
            lng: String(lng)
          });
          if (this.salesProduct) {
            qs.set("product", this.salesProduct);
          }
          const url = `${base}/geo-sales-rep?${qs.toString()}`;
          const headers = {};
          if (window.cirrusBlocks.restNonce) {
            headers["X-WP-Nonce"] = window.cirrusBlocks.restNonce;
          }
          fetch(url, { headers, credentials: "same-origin" })
            .then((response) => {
              if (!response.ok) {
                throw new Error("Nearest sales rep request failed");
              }
              return response.json();
            })
            .then((data) => {
              if (this.showRepOnSubmit && this.submitted) {
                this.geoLookupPending = false;
                return;
              }
              this.applyRepPayload(data);
              const url = this.profilePhotoUrl || this.repFallbackBgUrl;
              return csaPreloadRepBackgroundUrl(url)
                .then(() => {
                  this.geoRepVisible = true;
                  this.geoLookupPending = false;
                })
                .catch(() => {
                  this.geoRepVisible = true;
                  this.geoLookupPending = false;
                });
            })
            .catch(() => {
              this.tryApplyGeoFallback();
            });
        },
        () => {
          this.tryApplyGeoFallback();
        },
        {
          enableHighAccuracy: false,
          timeout: 12000,
          maximumAge: 300000
        }
      );
    },

    openModal() {
      this.open = true;

      const gform_wrapper = this.$el.querySelector(".gform_wrapper");
      if (gform_wrapper) {
        gform_wrapper.style.display = "block";
      }

      // Find and hide external close buttons
      const allCloseButtons = document.querySelectorAll(
        'button[aria-label="Close modal"]'
      );
      const blockElement = this.$el;
      this.hiddenCloseButtons = [];

      allCloseButtons.forEach((button) => {
        // Check if the button is outside the current block
        if (!blockElement.contains(button)) {
          // Store the element and its original display value
          const originalDisplay = button.style.display || "";
          this.hiddenCloseButtons.push({
            element: button,
            originalDisplay: originalDisplay
          });
          // Hide the button
          button.style.display = "none";
        }
      });

      if (this.geoSalesRepEnabled) {
        if (this.geoShowFallbackRep && this.canFetchGeoFallback()) {
          this.fetchGeoFallbackRep();
        }
        this.fetchGeoSalesRep();
      }
    },
    closeModal() {
      this.open = false;

      // Restore hidden close buttons
      this.hiddenCloseButtons.forEach(({ element, originalDisplay }) => {
        // Only restore if element still exists in DOM
        if (element && document.contains(element)) {
          if (originalDisplay) {
            element.style.display = originalDisplay;
          } else {
            // If there was no original display value, remove the inline style
            element.style.display = "";
          }
        }
      });
      // Clear the array
      this.hiddenCloseButtons = [];
    },
    onSubmit() {
      this.submitted = true;
      const url = `country=${this.userCountry}&state=${this.userState}&zip=${this.userZip}&product=${this.salesProduct}`;
      if (url && url !== this.prevParams) {
        this.fetch();
      }
    },
    setData(detail) {
      this.userCountry =
        detail.userCountry || sessionStorage.getItem("userCountry") || "";
      this.userState =
        detail.userState || sessionStorage.getItem("userState") || "";
      this.userZip = detail.userZip || sessionStorage.getItem("userZip") || "";
      this.salesProduct =
        detail.salesProduct || sessionStorage.getItem("salesProduct") || "";

      sessionStorage.setItem("userCountry", this.userCountry);
      sessionStorage.setItem("userState", this.userState);
      sessionStorage.setItem("userZip", this.userZip);
      sessionStorage.setItem("salesProduct", this.salesProduct);

      if (this.userCountry && this.userZip && this.userCountry) {
        this.fetch();
      }
    },
    fetch() {
      if (
        this.userCountry &&
        this.userZip &&
        this.userState &&
        this.salesProduct &&
        window.cirrusBlocks?.restUrl
      ) {
        const url = `${window.cirrusBlocks.restUrl}/contact-sales-associate?country=${this.userCountry}&state=${this.userState}&zip=${this.userZip}&product=${this.salesProduct}`;
        this.prevParams = `country=${this.userCountry}&state=${this.userState}&zip=${this.userZip}&product=${this.salesProduct}`;

        if (url)
          return fetch(url)
            .then((response) => {
              if (!response.ok) {
                throw new Error("Network response was not ok");
              }
              return response.json();
            })
            .then((data) => {
              this.applyRepPayload(data);
            })
            .catch((error) => {
              // Error handling for fetch operation
            });
      }
    }
  });
}

/**
 * Register Alpine.js component
 * @returns {Promise} Resolves when component registration is complete
 */
function registerAlpineComponent() {
  document.addEventListener("alpine:init", () => {
    window.Alpine.data(
      "cirrusSalesAssociate",
      getCirrusSalesAssociateComponent()
    );
  });
}

/**
 * Initialize contact sales associate block
 * @param {Document|HTMLElement} container - Container to search for blocks within
 */
function initializeContactSalesAssociate(container = document) {
  // Register Alpine component first (only once)
  registerAlpineComponent();

  // Find all block instances within the container
  const blocks = container.querySelectorAll('[x-data="cirrusSalesAssociate"]');

  blocks.forEach((block, index) => {
    const blockId = block.getAttribute("id");
    // Set up modal click handler
    block.addEventListener("click", (e) => {
      e.stopPropagation();

      const wrapper = block.querySelector(".gform_wrapper");
      if (!wrapper) {
        return;
      }

      const form = wrapper.querySelector("form");
      if (!form) {
        return;
      }

      form.style.opacity = 1;
    });

    // Configure labels
    csaConfigureLabels(block);

    // Get form ID from the form element
    const form = block.querySelector("form");
    let formId = null;
    if (form) {
      const formIdMatch = form.id.match(/gform_(\d+)/);
      if (formIdMatch) {
        formId = parseInt(formIdMatch[1], 10);
      }
    }

    // GravityForms JS hooks require using the global jQuery object
    if (typeof jQuery !== "undefined") {
      jQuery(document).on(
        "gform_post_render",
        function (event, renderedFormId, currentPage) {
          if (formId && renderedFormId === formId) {
            csaSyncProgressTitle(block, formId, currentPage);
            setTimeout(() => csaSyncProgressTitle(block, formId, currentPage), 0);

            // Initialize Google Maps if available
            if (
              typeof google !== "undefined" &&
              google.maps &&
              google.maps.places
            ) {
              initMap(block);
            }

            // Configure the labels on all pages
            csaConfigureLabels(block);

            // Listen to the fields
            csaListenToFields(block);

            // Ensure the country dropdown opens upwards
            const phoneInput = block.querySelector(".gfield--type-phone input");
            if (phoneInput) {
              phoneInput.addEventListener("open:countrydropdown", function (e) {
                const countryList = document.querySelector(".iti__country-list");
                if (countryList) {
                  countryList.classList.add("iti__country-list--dropup");
                }
              });
            }

            // Dispatch the event with filtered data
            block.dispatchEvent(
              new CustomEvent("csa-set-data", {
                detail: csaGetFields(block)
              })
            );
          }
        }
      );

      // Set up modal trigger links
      jQuery(document).ready(function () {
        document.querySelectorAll(`a[href="#${blockId}"]`).forEach((el) => {
          el.setAttribute("href", "javascript:void(0);");
          el.addEventListener("click", () => {
            block.dispatchEvent(new CustomEvent("csa-open-modal"));
          });
        });

        // Handle form confirmation
        if (formId) {
          jQuery(document).on(
            "gform_confirmation_loaded",
            function (event, confirmedFormId) {
              if (confirmedFormId === formId) {
                // Send a form submission event to our Alpine instance
                block.dispatchEvent(new CustomEvent("csa-submit"));
              }
            }
          );
        }
      });
    }
  });
}

initializeContactSalesAssociate();
